/**
 * Left-rail panel for V2 that lists tasks with no schedule event yet.
 * Search, filter-by-priority, and drag-to-grid. On mobile this collapses
 * into a bottom drawer the caller controls via the `open` prop.
 */

import React, { useMemo, useState } from 'react'
import { Search, Inbox, ChevronDown, ChevronRight } from 'lucide-react'
import type { Task } from '../../services/taskService'
import { DraggableTaskCard } from './DraggableTaskCard'

interface UnscheduledTasksSidebarProps {
  tasks: Task[]
  loading: boolean
  // Set when a drag is active; the card matching this id dims to act as
  // the "source" placeholder while the DragOverlay renders the ghost.
  draggingTaskId?: number | null
  /** Card to ring + scroll to (arrived via Plan it → Pick a time). */
  highlightTaskId?: number | null
  collapsed?: boolean
  onToggleCollapse?: () => void
}

type PriorityFilter = 'all' | 'high' | 'medium' | 'low'

export const UnscheduledTasksSidebar: React.FC<UnscheduledTasksSidebarProps> = ({
  tasks,
  loading,
  draggingTaskId,
  highlightTaskId = null,
  collapsed = false,
  onToggleCollapse,
}) => {
  const [query, setQuery] = useState('')
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return tasks.filter((t) => {
      if (q && !t.title.toLowerCase().includes(q)) return false
      if (priorityFilter === 'all') return true
      if (priorityFilter === 'high') {
        return t.priority === 'high' || t.priority === 'urgent'
      }
      return t.priority === priorityFilter
    })
  }, [tasks, query, priorityFilter])

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggleCollapse}
        className="hidden lg:flex flex-col items-center justify-center gap-2 w-10 border-r border-soft bg-surface text-muted hover:text-primary"
        aria-label="Expand unscheduled tasks"
      >
        <Inbox className="h-4 w-4" />
        <span className="[writing-mode:vertical-rl] text-xs">Unscheduled ({tasks.length})</span>
        <ChevronRight className="h-4 w-4" />
      </button>
    )
  }

  return (
    <aside className="flex flex-col w-72 max-w-[85vw] flex-shrink-0 h-full border-r border-soft bg-surface">
      <header className="flex items-center justify-between px-3 py-2 border-b border-soft">
        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
          <Inbox className="h-4 w-4" />
          Unscheduled
          <span className="text-xs font-normal text-muted">({tasks.length})</span>
        </div>
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label="Collapse sidebar"
            className="p-1 rounded hover:bg-surface-alt text-muted hover:text-primary"
          >
            <ChevronDown className="h-4 w-4 rotate-90" />
          </button>
        )}
      </header>

      <div className="p-3 space-y-2 border-b border-soft">
        <label className="relative block">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks…"
            className="w-full rounded-md border border-soft bg-default pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:border-brand"
          />
        </label>
        <div className="flex gap-1 text-xs">
          {(['all', 'high', 'medium', 'low'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPriorityFilter(p)}
              className={`px-2 py-1 rounded-full border transition-colors ${
                priorityFilter === p
                  ? 'bg-brand text-on-brand border-brand'
                  : 'bg-surface text-secondary border-soft hover:border-strong'
              }`}
            >
              {p === 'high' ? 'High/urgent' : p[0].toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="text-center text-sm text-muted py-6">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-sm text-muted py-6">
            {tasks.length === 0
              ? 'Nothing unscheduled. Nice.'
              : 'No tasks match those filters.'}
          </div>
        ) : (
          filtered.map((t) => (
            <DraggableTaskCard
              key={t.id}
              task={t}
              isBeingDragged={draggingTaskId === t.id}
              highlighted={highlightTaskId === t.id}
            />
          ))
        )}
      </div>
    </aside>
  )
}

export default UnscheduledTasksSidebar
